import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import LoadingState from './LoadingState';

// Helper to get default date range (Year-To-Date)
function getDefaultDates() {
  const end = new Date();
  const start = new Date(end.getFullYear(), 0, 1); // January 1st of current year
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
}

function AnalyticsNew({ user, managedOwner }) {
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data whenever dates or user changes
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!user?.role) return;

      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        
        // Determine endpoints based on role
        const isOwner = user.role === 'OWNER' || user.role === 'owner' || managedOwner;
        let dashboardUrl = isOwner
          ? `${baseUrl}/api/analytics/owner/dashboard?startDate=${startDate}&endDate=${endDate}`
          : `${baseUrl}/api/analytics/dealerships/${user.dealershipId}/dashboard?startDate=${startDate}&endDate=${endDate}`;
        
        // Add ownerId parameter if admin is managing owner
        if (managedOwner) {
          dashboardUrl += `&ownerId=${managedOwner.id}`;
        }

        // Fetch dashboard data
        const dashboardRes = await fetch(dashboardUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!dashboardRes.ok) throw new Error('Failed to fetch dashboard data');
        const dashboardData = await dashboardRes.json();

        // Fetch trends data
        const daysDiff = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
        const groupBy = daysDiff > 90 ? 'month' : daysDiff > 30 ? 'week' : 'day';
        
        let trendsUrl = isOwner
          ? `${baseUrl}/api/analytics/owner/trends/sales?startDate=${startDate}&endDate=${endDate}&groupBy=${groupBy}`
          : `${baseUrl}/api/analytics/dealerships/${user.dealershipId}/trends/sales?startDate=${startDate}&endDate=${endDate}&groupBy=${groupBy}`;
        
        // Add ownerId parameter if admin is managing owner
        if (managedOwner) {
          trendsUrl += `&ownerId=${managedOwner.id}`;
        }

        const trendsRes = await fetch(trendsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        let trendsData = null;
        if (trendsRes.ok) {
          trendsData = await trendsRes.json();
        }

        if (!cancelled) {
          setData({ dashboard: dashboardData, trends: trendsData });
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Analytics fetch error:', err);
          setError(err.message);
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [user?.role, user?.dealershipId, startDate, endDate, managedOwner]);

  // Format period labels for charts
  function formatPeriod(period) {
    if (!period) return '';
    if (period.includes('-W')) return period.substring(5);
    if (period.length === 7) {
      const [year, month] = period.split('-');
      return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short' });
    }
    if (period.length === 10) {
      return new Date(period).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return period;
  }

  // Prepare chart data
  function getChartData() {
    if (!data?.dashboard) return null;

    const { dashboard, trends } = data;
    
    // Revenue trend data
    const revenueData = trends?.trends?.length > 0
      ? trends.trends.map(t => ({
          period: formatPeriod(t.period),
          revenue: t.revenue || 0,
          units: t.units || 0,
          grossProfit: t.grossProfit || 0
        }))
      : [];

    // Parts vs Labor pie chart
    const partsVsLabor = [
      { name: 'Parts', value: dashboard.parts?.totalRevenue || dashboard.totals?.totalServiceRevenue * 0.4 || 0 },
      { name: 'Labor', value: dashboard.service?.totalLaborRevenue || dashboard.totals?.totalServiceRevenue * 0.6 || 0 }
    ];

    // F&I data
    const fiData = revenueData.map(rd => ({
      period: rd.period,
      income: rd.revenue * 0.03,
      penetration: rd.units > 0 ? 40 : 0
    }));

    return { revenueData, partsVsLabor, fiData };
  }

  const chartData = getChartData();

  if (loading) {
    return <LoadingState message="Loading analytics data..." fullscreen />;
  }

  if (error) {
    return (
      <div>
        <div className="view-header">
          <h1 className="view-title">Analytics</h1>
          <p className="view-subtitle">Unable to load analytics</p>
        </div>
        <div className="content-card">
          <p style={{ color: '#ef4444' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="view-header">
        <h1 className="view-title">Analytics</h1>
        <p className="view-subtitle">No data available</p>
      </div>
    );
  }

  const { revenueData, partsVsLabor, fiData } = chartData;
  const isOwner = user?.role === 'OWNER' || user?.role === 'owner';
  const COLORS = ['#10b981', '#3b82f6', '#f97316', '#f59e0b', '#ef4444', '#06b6d4'];

  return (
    <div>
      <div className="view-header">
        <h1 className="view-title">Analytics</h1>
        <p className="view-subtitle">
          {isOwner
            ? 'Network-wide performance trends and insights'
            : `Performance trends for ${user.dealershipName || 'your dealership'}`}
        </p>
        
        <div className="date-range-selector">
          <div className="date-input-wrapper">
            <label htmlFor="startDate" className="date-label">Start:</label>
            <div className="date-picker-group">
              <span className="date-display">{startDate}</span>
              <label htmlFor="startDate" className="calendar-icon-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input-hidden"
              />
            </div>
          </div>
          <div className="date-input-wrapper">
            <label htmlFor="endDate" className="date-label">End:</label>
            <div className="date-picker-group">
              <span className="date-display">{endDate}</span>
              <label htmlFor="endDate" className="calendar-icon-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input-hidden"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Trends */}
      <div className="content-card">
        <h2 className="card-title">Revenue Trends</h2>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={revenueData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
              </linearGradient>
              <filter id="shadow">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#10b981" floodOpacity="0.5"/>
              </filter>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
            <XAxis 
              dataKey="period" 
              stroke="rgba(255,255,255,0.4)"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <YAxis 
              stroke="rgba(255,255,255,0.4)"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                padding: '12px'
              }}
              formatter={(value) => [`$${Math.round(value).toLocaleString()}`, 'Revenue']}
              labelStyle={{ color: '#10b981', fontWeight: 'bold', marginBottom: '4px' }}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#10b981"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
              filter="url(#shadow)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Parts vs Labor */}
      <div className="content-card">
        <h2 className="card-title">Parts vs. Labor Revenue</h2>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <defs>
              <filter id="donutShadow">
                <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#000" floodOpacity="0.3"/>
              </filter>
            </defs>
            <Pie
              data={partsVsLabor}
              cx="40%"
              cy="50%"
              labelLine={false}
              innerRadius={90}
              outerRadius={130}
              fill="#8884d8"
              dataKey="value"
              paddingAngle={3}
              filter="url(#donutShadow)"
            >
              {partsVsLabor.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[index % COLORS.length]}
                  stroke="rgba(0,0,0,0.2)"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => `$${Math.round(value).toLocaleString()}`}
              contentStyle={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                color: '#ffffff',
                padding: '12px'
              }}
              itemStyle={{
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500'
              }}
              labelStyle={{
                color: '#3b82f6',
                fontWeight: 'bold'
              }}
            />
            <Legend
              layout="vertical"
              verticalAlign="middle"
              align="right"
              iconType="circle"
              wrapperStyle={{ 
                paddingLeft: '30px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* F&I Performance */}
      <div className="content-card">
        <h2 className="card-title">F&I Performance</h2>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={fiData} margin={{ top: 20, right: 40, left: 20, bottom: 20 }} barGap={8}>
            <defs>
              <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={1}/>
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.7}/>
              </linearGradient>
              <linearGradient id="penetrationGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f97316" stopOpacity={1}/>
                <stop offset="100%" stopColor="#f97316" stopOpacity={0.7}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
            <XAxis 
              dataKey="period" 
              stroke="rgba(255,255,255,0.4)"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <YAxis 
              yAxisId="left" 
              stroke="rgba(255,255,255,0.4)"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              stroke="rgba(255,255,255,0.4)"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickFormatter={(value) => `${Math.round(value)}%`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                padding: '12px',
                color: '#ffffff'
              }}
              itemStyle={{
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500'
              }}
              formatter={(value, name) => {
                if (name === 'F&I Income') return [`$${Math.round(value).toLocaleString()}`, name];
                if (name === 'Penetration %') return [`${Math.round(value)}%`, name];
                return value;
              }}
              labelStyle={{ color: '#f97316', fontWeight: 'bold', marginBottom: '4px' }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '14px', fontWeight: '500', paddingTop: '10px', color: '#ffffff' }}
              iconType="circle"
            />
            <Bar 
              yAxisId="left" 
              dataKey="income" 
              fill="url(#incomeGradient)"
              name="F&I Income"
              radius={[8, 8, 0, 0]}
              maxBarSize={60}
            />
            <Bar 
              yAxisId="right" 
              dataKey="penetration" 
              fill="url(#penetrationGradient)"
              name="Penetration %"
              radius={[8, 8, 0, 0]}
              maxBarSize={60}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default AnalyticsNew;
